import { useEffect, useMemo, useState } from "react";
import UserQuickView from "../../components/users/UserQuickView";
import useAllUsers from "../../utils/hooks/user/useAllUsers";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { formatRole } from "../../utils/hooks/multi-tenant/invitation";
import Toggle from "../../components/forms/inputs/Toggle";
import ProfileQuickLook from "../../components/profile/ProfileQuickLook";

import SearchIcon from "../../assets/icons/SearchIcon";
import CompanyFilterDropdown from "../../components/filters/CompanyFilterDropdown";
import { useGetAllUsersQuery } from "../../utils/api/endpoints/usersApi";

type RoleTab = "all" | "researcher" | "business" | "admin";

const ROLE_TAB_CONFIG: { key: RoleTab; label: string; roles?: UserRole[] }[] = [
  { key: "all", label: "All Roles" },
  { key: "researcher", label: "Researcher", roles: [UserRole.RESEARCHER] },
  { key: "business", label: "Business Owner", roles: [UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER] },
  { key: "admin", label: "Admin", roles: [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA] },
];

const getRoleBadgeStyle = (role: UserRole) => {
  switch (role) {
    case UserRole.RESEARCHER:
      return { backgroundColor: "#EEF2FF", color: "#4F46E5", border: "1px solid #C7D2FE" };
    case UserRole.BUSINESS:
    case UserRole.BUSINESS_MANAGER:
    case UserRole.BUSINESS_ADMINISTRATOR:
    case UserRole.DEVELOPER:
      return { backgroundColor: "#ECFDF5", color: "#059669", border: "1px solid #A7F3D0" };
    case UserRole.ADMIN:
    case UserRole.SUB_ADMIN:
    case UserRole.ADMIN_MANAGER:
    case UserRole.QA:
      return { backgroundColor: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE" };
    default:
      return { backgroundColor: "#F3F4F6", color: "#6B7280", border: "1px solid #D1D5DB" };
  }
};

const getRoleTabIcon = (key: RoleTab) => {
  switch (key) {
    case "all":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case "researcher":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
    case "business":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        </svg>
      );
    case "admin":
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      );
  }
};

const PaginatedUsers = () => {
  const [selectedId, setSelectedId] = useState<number>();
  const [activeTab, setActiveTab] = useState<RoleTab>("all");
  const [search, setSearch] = useState("");
  const {
    users,
    filters,
    count,
    isError,
    isLoading,
    setFilters,
    setPage,
    updateUserStatus,
  } = useAllUsers();

  usePageTitle("Manage Users");

  const selectedUser = useMemo(() => {
    if (selectedId && users) {
      const user = users.filter(user => user && user.id === selectedId);
      return user.length > 0 ? user[0] : undefined;
    }
  }, [users, selectedId]);

  // Fetch accurate total counts per role group (limit=1, only need count)
  const { data: allData } = useGetAllUsersQuery({ limit: 1 });
  const { data: researcherData } = useGetAllUsersQuery({
    limit: 1,
    roles: [UserRole.RESEARCHER],
  });
  const { data: businessData } = useGetAllUsersQuery({
    limit: 1,
    roles: [UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER],
  });
  const { data: adminData } = useGetAllUsersQuery({
    limit: 1,
    roles: [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER, UserRole.QA],
  });

  // Accurate counts per role tab
  const roleCounts = useMemo(() => ({
    all: allData?.count ?? 0,
    researcher: researcherData?.count ?? 0,
    business: businessData?.count ?? 0,
    admin: adminData?.count ?? 0,
  }), [allData, researcherData, businessData, adminData]);

  // Handle tab change
  const handleTabChange = (tab: RoleTab) => {
    setActiveTab(tab);
    const config = ROLE_TAB_CONFIG.find(t => t.key === tab);
    setFilters(f => ({
      ...f,
      roles: config?.roles || undefined,
      page: 0,
    }));
  };

  // Handle search with debounce
  const handleSearchChange = (value: string) => {
    setSearch(value);
    // Debounce is handled below
  };

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(f => ({
        ...f,
        search: search === "" ? undefined : search,
      }));
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const totalPages = Math.ceil(count / (filters.limit || 5));
  const currentPage = filters.page || 0;
  const startItem = currentPage * (filters.limit || 5) + 1;
  const endItem = Math.min((currentPage + 1) * (filters.limit || 5), count);

  return (
    <div className="flex h-full flex-col" style={{ backgroundColor: "#f9fafb" }}>
      {/* Header */}
      <header className="px-6 pt-8 pb-2" style={{ backgroundColor: "#f9fafb" }}>
        <h1 className="text-2xl font-bold" style={{ color: "#0B1739" }}>
          Manage Users
        </h1>
        <p className="mt-1 text-sm" style={{ color: "#6B7280" }}>
          Manage and monitor all platform users across your organisation.
        </p>
      </header>

      {/* Filters Container — role tabs + search + status dropdown */}
      <div className="px-6 pt-4 pb-4" style={{ backgroundColor: "#f9fafb" }}>
        <div className="rounded-xl shadow-sm" style={{ border: "1px solid #E5E7EB" }}>

          {/* Dark header — Role Tabs */}
          <div className="px-6 py-5 rounded-t-xl" style={{ backgroundColor: "#010D2C" }}>
            <div className="flex items-center gap-2">
              {ROLE_TAB_CONFIG.map(tab => {
                const isActive = activeTab === tab.key;
                const tabCount = roleCounts[tab.key];
                return (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-all"
                    style={{
                      backgroundColor: isActive ? "rgba(88, 204, 2, 0.20)" : "transparent",
                      color: isActive ? "#58CC02" : "#FFFFFF",
                      border: isActive ? "0.5px solid rgba(88, 204, 2, 0.80)" : "0.5px solid transparent",
                      borderBottom: isActive ? "0.5px solid rgba(88, 204, 2, 0.80)" : "1.5px solid rgba(88, 204, 2, 0.22)"
                    }}
                  >
                    {getRoleTabIcon(tab.key)}
                    <span>{tab.label}</span>
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-semibold"
                      style={{
                        backgroundColor: isActive ? "#58CC02" : "#F1F5F9",
                        color: isActive ? "#FFFFFF" : "#58CC02",
                      }}
                    >
                      {tabCount}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* White section — Search + Filters */}
          <div className="px-6 py-5 bg-white rounded-b-xl">
            <div className="flex items-center gap-4">
              <div className="relative flex-grow" style={{ maxWidth: "540px" }}>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  className="w-full rounded-lg border px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "#D1D5DB",
                    color: "#374151",
                  }}
                  value={search}
                  onChange={e => {
                    setSearch(e.target.value);
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      setFilters(f => ({
                        ...f,
                        search: search === "" ? undefined : search,
                      }));
                    }
                  }}
                />
                <SearchIcon
                  className="absolute right-3 top-1/2 -translate-y-1/2 stroke-gray-400 hover:cursor-pointer"
                  onClick={() =>
                    setFilters(f => ({
                      ...f,
                      search: search === "" ? undefined : search,
                    }))
                  }
                />
              </div>
              <div style={{ minWidth: "240px" }}>
                <CompanyFilterDropdown
                  selectedIds={filters.companyIds || []}
                  onChange={(ids) =>
                    setFilters(f => ({
                      ...f,
                      companyIds: ids.length > 0 ? ids : undefined,
                      page: 0,
                    }))
                  }
                />
              </div>
              <div>
                <select
                  value={filters.active?.[0] || ""}
                  onChange={(e) =>
                    setFilters(f => ({
                      ...f,
                      active: e.target.value ? [e.target.value as "Active" | "Inactive"] : undefined,
                    }))
                  }
                  className="block w-48 rounded-lg border px-4 py-3 text-sm appearance-none pr-10"
                  style={{
                    borderColor: "#D1D5DB",
                    color: "#0B1739",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%230B1739' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "12px 12px",
                  }}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div>
                <select
                  value={filters.mfa?.[0] || ""}
                  onChange={(e) =>
                    setFilters(f => ({
                      ...f,
                      mfa: e.target.value ? [e.target.value as "TOTP" | "Email" | "Disabled"] : undefined,
                      page: 0,
                    }))
                  }
                  className="block w-48 rounded-lg border px-4 py-3 text-sm appearance-none pr-10"
                  style={{
                    borderColor: "#D1D5DB",
                    color: "#0B1739",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%230B1739' d='M6 9L2 5h8z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 0.75rem center",
                    backgroundSize: "12px 12px",
                  }}
                >
                  <option value="">All MFA</option>
                  <option value="TOTP">MFA (TOTP)</option>
                  <option value="Email">Email Only</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
            </div>
          </div>

        </div>
      </div>


      {/* Table */}
      <div className="flex-grow overflow-auto px-6 pb-4">
        <div className="overflow-hidden rounded-lg shadow-sm" style={{ border: "1px solid #E5E7EB" }}>
          {/* Table Header */}
          <div
            className="grid items-center px-6 py-4 text-sm font-medium"
            style={{
              gridTemplateColumns: "2.4fr 1.6fr 1.2fr 1.2fr 1fr",
              backgroundColor: "#010D2C",
              color: "#FFFFFF",
            }}
          >
            <span>Name</span>
            <span>Role</span>
            <span>MFA</span>
            <span>Status</span>
            <span>Actions</span>
          </div>

          {/* Table Body */}
          <div style={{ backgroundColor: "#FFFFFF" }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-sm text-gray-400">
                Loading users...
              </div>
            ) : isError ? (
              <div className="flex items-center justify-center py-16 text-sm text-red-400">
                Failed to load users...
              </div>
            ) : users?.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-sm text-gray-400">
                No users found
              </div>
            ) : (
              users?.map(
                (user, index) =>
                  user && (
                    <div
                      key={user.id || index}
                      className="grid items-center border-t px-6 py-4 transition-colors hover:bg-gray-50"
                      style={{
                        gridTemplateColumns: "2.4fr 1.6fr 1.2fr 1.2fr 1fr",
                        borderColor: "#F3F4F6",
                      }}
                    >
                      {/* Name with avatar */}
                      <ProfileQuickLook
                        title={user?.displayName || user?.username || "Username"}
                        subtitle={user?.email || "email@example.com"}
                        imageSrc={user?.profilePicture}
                        imageClassName="bg-ctb-blue-50"
                        titleClassName="font-medium"
                        subtitleClassName="!text-sm text-slate-500 font-light"
                      />

                      {/* Role Badge */}
                      <div>
                        <span
                          className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                          style={getRoleBadgeStyle(user.role)}
                        >
                          {formatRole(user.role as UserRole)}
                        </span>
                      </div>

                      {/* MFA Badge */}
                      <div>
                        <span
                          className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                          style={
                            user.totpConfirmedAt
                              ? { backgroundColor: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" }
                              : user.twoFaEnabled
                              ? { backgroundColor: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE" }
                              : { backgroundColor: "#F9FAFB", color: "#6B7280", border: "1px solid #E5E7EB" }
                          }
                        >
                          {user.totpConfirmedAt ? "MFA (TOTP)" : user.twoFaEnabled ? "Email Only" : "Disabled"}
                        </span>
                      </div>

                      {/* Status Toggle */}
                      <div className="flex items-center gap-3">
                        <Toggle
                          value={user.approved || false}
                          onChange={(value) => updateUserStatus(user.id, value)}
                          disabled={user.role === UserRole.ADMIN}
                          activeColor="#58CC02"
                          activeTrackColor="rgba(88, 204, 2, 0.20)"
                        />
                        <span
                          className="text-sm font-medium"
                          style={{
                            color: user.approved ? "#58CC02" : "#6B7280",
                          }}
                        >
                          {user.approved ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {/* Actions */}
                      <div>
                        <button
                          onClick={() => setSelectedId(user.id)}
                          className="rounded-md px-5 py-2 text-sm font-medium transition hover:cursor-pointer"
                          style={{
                            backgroundColor: "#010D2C",
                            color: "#FFFFFF",
                            border: "none",
                          }}
                          onMouseEnter={e => {
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#0B1739";
                          }}
                          onMouseLeave={e => {
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#010D2C";
                          }}
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  )
              )
            )}
          </div>
          {/* Pagination — inside the table card */}
          {count > 0 && (() => {
            const totalPages = Math.ceil(count / (filters.limit || 5));
            if (totalPages <= 1) return null;

            // Build visible page items: always show first, last, current and its neighbors
            const pageItems: (number | "...")[] = [];
            const addPage = (p: number) => {
              if (!pageItems.includes(p)) pageItems.push(p);
            };
            addPage(0);
            if (currentPage - 1 > 0) addPage(currentPage - 1);
            addPage(currentPage);
            if (currentPage + 1 < totalPages - 1) addPage(currentPage + 1);
            addPage(totalPages - 1);
            pageItems.sort((a, b) => (a as number) - (b as number));

            // Insert ellipsis where gaps exist
            const withEllipsis: (number | "...")[] = [];
            pageItems.forEach((item, i) => {
              if (i > 0) {
                const prev = pageItems[i - 1] as number;
                if ((item as number) - prev > 1) withEllipsis.push("...");
              }
              withEllipsis.push(item);
            });

            const btnBase: React.CSSProperties = {
              border: "1px solid #E5E7EB",
              backgroundColor: "#FFFFFF",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 500,
              height: "34px",
              cursor: "pointer",
            };

            return (
              <div
                className="flex items-center justify-between px-6 py-4"
                style={{ borderTop: "1px solid #E5E7EB" }}
              >
                <p className="text-sm" style={{ color: "#6B7280" }}>
                  Showing {startItem} to {endItem} of {count} results
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    style={{
                      ...btnBase,
                      padding: "0 14px",
                      color: currentPage === 0 ? "#D1D5DB" : "#374151",
                      cursor: currentPage === 0 ? "default" : "pointer",
                    }}
                  >
                    Previous
                  </button>

                  {withEllipsis.map((item, i) =>
                    item === "..." ? (
                      <span
                        key={`ellipsis-${i}`}
                        style={{ color: "#9CA3AF", fontSize: "14px", padding: "0 4px" }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setPage(item as number)}
                        style={{
                          ...btnBase,
                          width: "34px",
                          textAlign: "center",
                          borderColor: currentPage === item ? "#4F46E5" : "#E5E7EB",
                          color: currentPage === item ? "#4F46E5" : "#374151",
                          backgroundColor: currentPage === item ? "#EEF2FF" : "#FFFFFF",
                        }}
                      >
                        {(item as number) + 1}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage >= totalPages - 1}
                    style={{
                      ...btnBase,
                      padding: "0 14px",
                      color: currentPage >= totalPages - 1 ? "#D1D5DB" : "#374151",
                      cursor: currentPage >= totalPages - 1 ? "default" : "pointer",
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>


      <UserQuickView
        user={selectedUser}
        onClose={() => setSelectedId(undefined)}
      />
    </div>
  );
};

export default PaginatedUsers;